import React, { useRef, useEffect } from 'react';
import { Edit3, X, Copy } from 'lucide-react';

interface TabContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  position: { x: number; y: number };
  canDelete: boolean;
}

const TabContextMenu: React.FC<TabContextMenuProps> = ({
  isOpen,
  onClose,
  onRename,
  onDuplicate,
  onDelete,
  position,
  canDelete,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[160px]"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <button
        onClick={onRename}
        className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
      >
        <Edit3 className="h-4 w-4" />
        Rename
      </button>
      <button
        onClick={onDuplicate}
        className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
      >
        <Copy className="h-4 w-4" />
        Duplicate
      </button>
      {canDelete && (
        <>
          <hr className="border-gray-200 dark:border-gray-600 my-1" />
          <button
            onClick={onDelete}
            className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Delete
          </button>
        </>
      )}
    </div>
  );
};

export default TabContextMenu;
